import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../lib/AuthContext'
import { loadMap, saveMap, getUserCompanyId } from '../lib/supabase'

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
    const s = document.createElement('script')
    s.src = src
    s.onload = resolve
    s.onerror = reject
    document.head.appendChild(s)
  })
}

async function exportToPDF(companyName, setExporting) {
  setExporting(true)
  try {
    await loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js')
    await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js')

    const chartEl = document.getElementById('org-chart-canvas')
    if (!chartEl) return

    // Temporarily reset transform for clean capture
    const origTransform = chartEl.style.transform
    chartEl.style.transform = 'none'

    const canvas = await window.html2canvas(chartEl, {
      scale: 1.5,
      backgroundColor: '#F5F2EC',
      useCORS: true,
      logging: false
    })

    chartEl.style.transform = origTransform

    const imgData = canvas.toDataURL('image/png')
    const { jsPDF } = window.jspdf
    const pageW = canvas.width / 1.5
    const pageH = canvas.height / 1.5 + 56
    const pdf = new jsPDF({ orientation: pageW > pageH ? 'landscape' : 'portrait', unit: 'pt', format: [pageW, pageH] })

    pdf.setFillColor(30, 51, 32)
    pdf.rect(0, 0, pageW, 48, 'F')
    pdf.setTextColor(255, 255, 255)
    pdf.setFontSize(16)
    pdf.setFont('helvetica', 'bold')
    pdf.text(companyName, 20, 26)
    pdf.setFontSize(9)
    pdf.setFont('helvetica', 'normal')
    pdf.setTextColor(180, 180, 180)
    pdf.text('Accountability Map  ·  The Hemlock Group', 20, 40)

    pdf.addImage(imgData, 'PNG', 0, 52, pageW, canvas.height / 1.5)
    pdf.save(`${companyName.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-accountability-map.pdf`)
  } finally {
    setExporting(false)
  }
}

const PAL = [
  { bg: '#E6F1FB', border: '#378ADD', title: '#0C447C', sub: '#185FA5' },
  { bg: '#EEEDFE', border: '#7F77DD', title: '#3C3489', sub: '#534AB7' },
  { bg: '#E1F5EE', border: '#1D9E75', title: '#085041', sub: '#0F6E56' },
  { bg: '#FAECE7', border: '#D85A30', title: '#712B13', sub: '#993C1D' },
  { bg: '#FBEAF0', border: '#D4537E', title: '#72243E', sub: '#993556' },
  { bg: '#FAEEDA', border: '#BA7517', title: '#633806', sub: '#854F0B' },
  { bg: '#EAF3DE', border: '#639922', title: '#27500A', sub: '#3B6D11' },
  { bg: '#F1EFE8', border: '#888780', title: '#444441', sub: '#5F5E5A' },
]

const DEFAULT_MAP = {
  companyName: 'Your Company',
  visionary: {
    id: 'vis', title: 'Visionary', owner: '', pal: 7,
    resp: ['20 ideas', 'Creativity / problem solving', 'Big relationships', 'Culture', 'R&D'],
    children: []
  },
  integrator: {
    id: 'int', title: 'Integrator', owner: '', pal: 7,
    resp: ['Lead, manage, accountability', 'Profit & loss / business plan', 'Remove obstacles & barriers', 'Special projects'],
    children: [
      { id: 1, title: 'Function', owner: '', pal: 0, resp: ['Add responsibilities'], children: [] }
    ]
  }
}

let nid = 100

export default function AccountabilityMap({ previewUserId }) {
  const { user: authUser } = useAuth()
  const user = previewUserId ? { ...authUser, id: previewUserId } : authUser
  const [companyId, setCompanyId] = useState(previewUserId || null)
  const [companyIdResolved, setCompanyIdResolved] = useState(!!previewUserId)
  const [mapData, setMapData] = useState(null)
  const [sel, setSel] = useState(null)
  const [saveStatus, setSaveStatus] = useState('saved')
  const [loading, setLoading] = useState(true)

  // Pan & zoom state
  const [exporting, setExporting] = useState(false)
  const [zoom, setZoom] = useState(0.85)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const isPanning = useRef(false)
  const panStart = useRef({ x: 0, y: 0 })
  const canvasRef = useRef(null)
  // Touch state — pinch + pan
  const touchState = useRef({ mode: null, lastDist: 0, startPan: { x: 0, y: 0 }, startTouch: { x: 0, y: 0 }, startZoom: 1 })
  // Mobile detection
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768)
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  // Resolve companyId (from preview prop, or from company_members by email)
  useEffect(() => {
    if (previewUserId) {
      setCompanyId(previewUserId)
      setCompanyIdResolved(true)
      return
    }
    if (!authUser?.email) return
    let alive = true
    getUserCompanyId(authUser.email).then(cid => {
      if (!alive) return
      setCompanyId(cid)
      setCompanyIdResolved(true)
    })
    return () => { alive = false }
  }, [authUser?.email, previewUserId])

  useEffect(() => {
    async function load() {
      if (!user || !companyIdResolved) return
      if (companyId) {
        const { data } = await loadMap(companyId)
        if (data?.map_data) {
          setMapData(data.map_data)
          setLoading(false)
          return
        }
      }
      const defaultMap = JSON.parse(JSON.stringify(DEFAULT_MAP))
      if (user.user_metadata?.company_name) {
        defaultMap.companyName = user.user_metadata.company_name
      }
      setMapData(defaultMap)
      setLoading(false)
    }
    load()
  }, [user, companyId, companyIdResolved])

  useEffect(() => {
    if (!mapData || loading) return
    if (previewUserId) return // read-only in admin preview
    if (!companyId) return
    setSaveStatus('unsaved')
    const timer = setTimeout(async () => {
      setSaveStatus('saving')
      await saveMap(companyId, mapData)
      setSaveStatus('saved')
    }, 2000)
    return () => clearTimeout(timer)
  }, [mapData]) // eslint-disable-line

  // Pan handlers
  function onMouseDown(e) {
    // Don't pan when clicking interactive elements or the header
    if (e.target.closest('.org-card')) return
    if (e.target.closest('button')) return
    if (e.target.closest('input')) return
    if (e.target.closest('select')) return
    if (e.target.closest('a')) return
    isPanning.current = true
    panStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y }
    e.currentTarget.style.cursor = 'grabbing'
  }
  function onMouseMove(e) {
    if (!isPanning.current) return
    setPan({ x: e.clientX - panStart.current.x, y: e.clientY - panStart.current.y })
  }
  function onMouseUp(e) {
    isPanning.current = false
    if (e.currentTarget) e.currentTarget.style.cursor = 'grab'
  }
  function onWheel(e) {
    e.preventDefault()
    setZoom(z => Math.min(2, Math.max(0.3, z - e.deltaY * 0.001)))
  }

  // --- Touch handlers ---
  function onTouchStart(e) {
    // Don't pan/zoom when starting touch on interactive elements
    const t = e.target
    if (t.closest('.org-card')) return
    if (t.closest('button') || t.closest('input') || t.closest('select') || t.closest('textarea') || t.closest('a')) return

    if (e.touches.length === 2) {
      // Pinch start
      const dx = e.touches[0].clientX - e.touches[1].clientX
      const dy = e.touches[0].clientY - e.touches[1].clientY
      touchState.current = {
        mode: 'pinch',
        lastDist: Math.hypot(dx, dy),
        startPan: { ...pan },
        startTouch: { x: 0, y: 0 },
        startZoom: zoom,
      }
    } else if (e.touches.length === 1) {
      const t0 = e.touches[0]
      touchState.current = {
        mode: 'pan',
        lastDist: 0,
        startPan: { ...pan },
        startTouch: { x: t0.clientX, y: t0.clientY },
        startZoom: zoom,
      }
    }
  }

  function onTouchMove(e) {
    const s = touchState.current
    if (!s.mode) return
    if (s.mode === 'pinch' && e.touches.length === 2) {
      e.preventDefault()
      const dx = e.touches[0].clientX - e.touches[1].clientX
      const dy = e.touches[0].clientY - e.touches[1].clientY
      const dist = Math.hypot(dx, dy)
      if (s.lastDist > 0) {
        const scale = dist / s.lastDist
        setZoom(z => Math.min(2, Math.max(0.3, z * scale)))
      }
      s.lastDist = dist
    } else if (s.mode === 'pan' && e.touches.length === 1) {
      e.preventDefault()
      const t0 = e.touches[0]
      setPan({
        x: s.startPan.x + (t0.clientX - s.startTouch.x),
        y: s.startPan.y + (t0.clientY - s.startTouch.y),
      })
    }
  }

  function onTouchEnd(e) {
    if (e.touches.length === 0) {
      touchState.current = { mode: null, lastDist: 0, startPan: { x: 0, y: 0 }, startTouch: { x: 0, y: 0 }, startZoom: 1 }
    } else if (e.touches.length === 1 && touchState.current.mode === 'pinch') {
      // Transition pinch → pan with remaining finger
      const t0 = e.touches[0]
      touchState.current = {
        mode: 'pan',
        lastDist: 0,
        startPan: { ...pan },
        startTouch: { x: t0.clientX, y: t0.clientY },
        startZoom: zoom,
      }
    }
  }

  function updateMap(fn) {
    setMapData(prev => {
      const next = JSON.parse(JSON.stringify(prev))
      fn(next)
      return next
    })
  }

  function allNodes(data) {
    if (!data) return []
    const nodes = []
    if (data.visionary) nodes.push(data.visionary)
    if (data.integrator) nodes.push(data.integrator)
    if (data.integrator) nodes.push(...flatAll(data.integrator))
    return nodes
  }
  function flatAll(n) { return (n.children || []).flatMap(c => [c, ...flatAll(c)]) }
  function findNode(data, id) { return allNodes(data).find(n => String(n.id) === String(id)) || null }
  function findParent(data, id) {
    if (!data.integrator) return null
    if ((data.integrator.children || []).find(c => String(c.id) === String(id))) return data.integrator
    for (const n of flatAll(data.integrator)) {
      if ((n.children || []).find(c => String(c.id) === String(id))) return n
    }
    return null
  }

  const selNode = mapData ? findNode(mapData, sel) : null

  function updateSelField(field, val) {
    if (!sel) return
    updateMap(draft => {
      const node = findNode(draft, sel)
      if (!node) return
      if (field === 'resp') node.resp = val.split('\n').map(s => s.trim()).filter(Boolean)
      else node[field] = val
    })
  }

  function addChild() {
    const parentId = sel && sel !== 'vis' && sel !== null ? sel : 'int'
    updateMap(draft => {
      const parent = findNode(draft, parentId)
      if (!parent || parent.id === 'vis') return
      parent.children = parent.children || []
      const newId = nid++
      parent.children.push({ id: newId, title: 'New function', owner: '', pal: parent.pal, resp: [], children: [] })
      setSel(String(newId))
    })
  }

  function deleteSelected() {
    if (!sel) return
    if (sel === 'vis') {
      // Remove visionary from map data entirely
      updateMap(draft => { draft.visionary = null })
      setSel(null)
      return
    }
    if (sel === 'int') {
      // Remove integrator — children move up to top level
      updateMap(draft => { draft.integrator = null })
      setSel(null)
      return
    }
    updateMap(draft => {
      const parent = findParent(draft, sel)
      if (parent) parent.children = parent.children.filter(c => String(c.id) !== String(sel))
    })
    setSel(null)
  }

  function resetView() {
    setZoom(0.85)
    setPan({ x: 0, y: 0 })
  }

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ fontSize: 14, color: '#888' }}>Loading your map…</div>
    </div>
  )

  const headerHeight = isMobile ? 56 : 72

  return (
    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', height: '100vh', overflow: 'hidden' }}>
      {/* Chart canvas */}
      <div
        ref={canvasRef}
        style={{ flex: 1, overflow: 'hidden', position: 'relative', cursor: 'grab', background: 'var(--color-background-tertiary)', touchAction: 'none' }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onWheel={onWheel}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      >
        {/* Header */}
        <div
          onMouseDown={e => e.stopPropagation()}
          onTouchStart={e => e.stopPropagation()}
          style={{
          position: 'absolute', top: 0, left: 0, right: 0, zIndex: 20,
          display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 16,
          padding: isMobile ? '10px 14px' : '20px 32px',
          background: 'var(--color-background-primary)',
          borderBottom: '1px solid var(--color-border-tertiary)',
          minHeight: headerHeight
        }}>
          <div style={{ minWidth: 0, flex: isMobile ? 1 : '0 1 auto' }}>
            <input
              value={mapData.companyName}
              onChange={e => updateMap(d => { d.companyName = e.target.value })}
              style={{
                fontSize: isMobile ? 16 : 22, fontWeight: 600, border: 'none', background: 'transparent',
                color: 'var(--color-text-primary)', outline: 'none',
                borderBottom: '2px solid transparent', padding: '2px 0',
                width: '100%', minWidth: 0
              }}
              onFocus={e => e.target.style.borderBottomColor = '#3B6D11'}
              onBlur={e => e.target.style.borderBottomColor = 'transparent'}
            />
            {!isMobile && <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 2 }}>Accountability map</div>}
          </div>
          <div style={{ marginLeft: isMobile ? 0 : 'auto', display: 'flex', alignItems: 'center', gap: isMobile ? 4 : 12, flexShrink: 0 }}>
            {!isMobile && (
              <span style={{
                fontSize: 11,
                color: saveStatus === 'saved' ? '#3B6D11' : saveStatus === 'saving' ? '#854F0B' : '#888'
              }}>
                {saveStatus === 'saved' && '✓ Saved'}
                {saveStatus === 'saving' && '↑ Saving…'}
                {saveStatus === 'unsaved' && '● Unsaved'}
              </span>
            )}
            {/* Zoom controls — compact on mobile */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <button onClick={() => setZoom(z => Math.max(0.3, z - 0.1))} style={zoomBtnStyle} aria-label="Zoom out">−</button>
              {!isMobile && <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', minWidth: 36, textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>}
              <button onClick={() => setZoom(z => Math.min(2, z + 0.1))} style={zoomBtnStyle} aria-label="Zoom in">+</button>
              <button onClick={resetView} style={{ ...zoomBtnStyle, marginLeft: 2, fontSize: 10, width: isMobile ? 'auto' : 28, padding: isMobile ? '0 8px' : 0 }}>Reset</button>
            </div>
            {!isMobile && (
              <button
                onClick={() => !exporting && exportToPDF(mapData.companyName || 'Company', setExporting)}
                style={{
                  fontSize: 12, padding: '7px 14px', borderRadius: 7,
                  border: '1px solid #D0CDC7', background: exporting ? '#F1EFE8' : 'transparent',
                  color: exporting ? '#aaa' : '#888780', fontWeight: 400, cursor: exporting ? 'default' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6
                }}
              >
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M6.5 1v7M3.5 5l3 3 3-3M1 9v1a2 2 0 002 2h7a2 2 0 002-2V9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>
                {exporting ? 'Exporting…' : 'Export PDF'}
              </button>
            )}
            <button
              onClick={addChild}
              style={{
                fontSize: isMobile ? 11 : 12, padding: isMobile ? '7px 10px' : '7px 14px', borderRadius: 7,
                border: '1px solid #3B6D11', background: 'transparent',
                color: '#3B6D11', fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap'
              }}
            >
              {isMobile ? '+ Add' : '+ Add function'}
            </button>
          </div>
        </div>

        {/* Zoomable/pannable canvas */}
        <div style={{
          position: 'absolute', top: headerHeight, left: 0, right: 0, bottom: 0,
          overflow: 'hidden'
        }}>
          <div id="org-chart-canvas" style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: 'top center',
            padding: isMobile ? '24px 16px' : '48px 64px',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            width: 'max-content', minWidth: '100%'
          }}>
            {mapData.visionary && <>
              <OrgCard node={mapData.visionary} sel={sel} onSelect={id => { setSel(id); }} />
              {mapData.integrator && <VLine color={PAL[mapData.visionary.pal].border} h={28} />}
            </>}
            {mapData.integrator && <>
              <OrgCard node={mapData.integrator} sel={sel} onSelect={id => { setSel(id); }} />
              <VLine color={PAL[mapData.integrator.pal].border} h={24} />
              <ChildRow nodes={mapData.integrator.children} parentPal={mapData.integrator.pal} sel={sel} onSelect={id => { setSel(id); }} />
            </>}
            {!mapData.integrator && mapData.visionary && (
              <ChildRow nodes={[]} parentPal={mapData.visionary.pal} sel={sel} onSelect={id => { setSel(id); }} />
            )}
          </div>
        </div>

        {/* Hint */}
        <div onMouseDown={e => e.stopPropagation()} style={{
          position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)',
          fontSize: 11, color: 'var(--color-text-tertiary)',
          background: 'var(--color-background-primary)',
          padding: '5px 12px', borderRadius: 999,
          border: '1px solid var(--color-border-tertiary)',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          maxWidth: 'calc(100% - 32px)',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {isMobile ? 'Pinch to zoom · Drag to pan · Tap a card' : 'Scroll to zoom · Drag to pan · Click a card to edit'}
        </div>
      </div>

      {/* Edit panel — side panel on desktop, bottom sheet on mobile */}
      {selNode && (
        <>
          {/* Backdrop on mobile */}
          {isMobile && (
            <div
              onClick={() => setSel(null)}
              style={{
                position: 'fixed', inset: 0, zIndex: 90,
                background: 'rgba(0,0,0,0.35)'
              }}
            />
          )}
          <div style={isMobile ? {
            position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 100,
            maxHeight: '80vh',
            borderTop: '3px solid #3B6D11',
            borderTopLeftRadius: 16, borderTopRightRadius: 16,
            background: '#FFFFFF', padding: 20,
            overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 16,
            boxShadow: '0 -8px 32px rgba(0,0,0,0.18)'
          } : {
            width: 300, borderLeft: '2px solid #3B6D11',
            background: '#FFFFFF', padding: 24,
            overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 16,
            flexShrink: 0, boxShadow: '-4px 0 16px rgba(0,0,0,0.08)'
          }}>
            {isMobile && (
              <div style={{ width: 36, height: 4, background: '#D0CDC7', borderRadius: 2, margin: '0 auto -4px' }} />
            )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--color-text-tertiary)' }}>
              Edit node
            </span>
            <button onClick={() => setSel(null)} style={{ border: 'none', background: 'none', fontSize: 18, color: '#aaa', padding: 0, cursor: 'pointer' }}>×</button>
          </div>

          <Field label="Function">
            <input value={selNode.title || ''} onChange={e => updateSelField('title', e.target.value)}
              style={inputStyle} placeholder="Function name" />
          </Field>

          <Field label="Owner">
            <input value={selNode.owner || ''} onChange={e => updateSelField('owner', e.target.value)}
              style={inputStyle} placeholder="Person's name" />
          </Field>

          <Field label="Responsibilities">
            <textarea
              value={(selNode.resp || []).join('\n')}
              onChange={e => {
                const val = e.target.value
                if (!sel) return
                updateMap(draft => {
                  const node = findNode(draft, sel)
                  if (!node) return
                  // Keep all lines including empty ones while typing
                  node.resp = val.split('\n')
                })
              }}
              onBlur={e => {
                // Clean up empty lines on blur
                const val = e.target.value
                if (!sel) return
                updateMap(draft => {
                  const node = findNode(draft, sel)
                  if (!node) return
                  node.resp = val.split('\n').map(s => s.trim()).filter(Boolean)
                })
              }}
              style={{ ...inputStyle, minHeight: 140, resize: 'vertical', lineHeight: 1.7 }}
              placeholder="One per line&#10;Press Enter to add more"
            />
            <div style={{ fontSize: 11, color: '#888780', marginTop: 4 }}>Press Enter for a new line</div>
          </Field>

          <Field label="Color">
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {PAL.map((p, i) => (
                <div key={i} onClick={() => updateSelField('pal', i)}
                  style={{
                    width: 22, height: 22, borderRadius: '50%', cursor: 'pointer',
                    background: p.bg, outline: `2px solid ${p.border}`, outlineOffset: 1,
                    border: selNode.pal === i ? `2px solid ${p.title}` : '2px solid transparent',
                    transition: 'transform .1s'
                  }}
                />
              ))}
            </div>
          </Field>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 4 }}>
            {sel !== 'vis' && <button onClick={addChild} style={btnStyle}>+ Add child</button>}
            <button onClick={deleteSelected} style={{ ...btnStyle, color: '#B91C1C', borderColor: '#FCA5A5' }}>
              Remove
            </button>
          </div>
          {(sel === 'vis' || sel === 'int') && (
            <div style={{ fontSize: 11, color: '#aaa', marginTop: 6 }}>
              {sel === 'vis' ? 'Removes the Visionary role.' : 'Removes the Integrator role. Functions below will remain.'}
            </div>
          )}
          </div>
        </>
      )}
    </div>
  )
}

function VLine({ color, h }) {
  return <div style={{ width: 2, height: h, background: color, opacity: .5, margin: '0 auto', flexShrink: 0 }} />
}

function OrgCard({ node, sel, onSelect }) {
  const p = PAL[node.pal || 0]
  const isSel = String(node.id) === String(sel)
  return (
    <div
      className="org-card"
      onClick={e => { e.stopPropagation(); onSelect(node.id) }}
      style={{
        width: 210, borderRadius: 10, padding: 14, cursor: 'pointer',
        background: p.bg,
        border: `${isSel ? 2 : 1.5}px solid ${isSel ? p.border : '#d0d0d0'}`,
        position: 'relative', zIndex: 2,
        boxShadow: isSel ? `0 0 0 3px ${p.border}33` : '0 1px 4px rgba(0,0,0,0.06)',
        userSelect: 'none'
      }}
    >
      <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', color: p.sub, marginBottom: 3 }}>
        {node.owner || 'No owner'}
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: p.title, marginBottom: node.resp?.length ? 6 : 0 }}>
        {node.title || 'Untitled'}
      </div>
      {node.resp?.length > 0 && <>
        <div style={{ height: 0.5, background: p.border, opacity: .4, margin: '5px 0' }} />
        <div style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.07em', color: p.sub, opacity: .7, marginBottom: 4 }}>
          Responsibilities
        </div>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {node.resp.map((r, i) => (
            <li key={i} style={{ fontSize: 11, color: p.sub, paddingLeft: 10, position: 'relative', lineHeight: 1.5, marginBottom: 2 }}>
              <span style={{ position: 'absolute', left: 0, color: '#aaa' }}>•</span>{r}
            </li>
          ))}
        </ul>
      </>}
    </div>
  )
}

function ChildRow({ nodes, parentPal, sel, onSelect }) {
  if (!nodes || nodes.length === 0) return null
  const p = PAL[parentPal || 0]
  return (
    <div style={{ position: 'relative', display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      {nodes.length > 1 && (
        <div style={{
          position: 'absolute', top: 0, left: 105, right: 105,
          height: 2, background: p.border, opacity: .4, zIndex: 1, pointerEvents: 'none'
        }} />
      )}
      {nodes.map(child => (
        <div key={child.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 2 }}>
          <VLine color={PAL[child.pal || 0].border} h={20} />
          <OrgCard node={child} sel={sel} onSelect={onSelect} />
          {child.children?.length > 0 && <>
            <VLine color={PAL[child.pal || 0].border} h={20} />
            <ChildRow nodes={child.children} parentPal={child.pal} sel={sel} onSelect={onSelect} />
          </>}
        </div>
      ))}
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div>
      <label style={{ fontSize: 11, fontWeight: 500, color: '#888780', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '.06em' }}>
        {label}
      </label>
      {children}
    </div>
  )
}

const zoomBtnStyle = {
  width: 28, height: 28, border: '1px solid var(--color-border-tertiary)',
  borderRadius: 6, background: 'var(--color-background-primary)',
  color: 'var(--color-text-secondary)', cursor: 'pointer', fontSize: 14,
  display: 'flex', alignItems: 'center', justifyContent: 'center'
}

const inputStyle = {
  width: '100%', padding: '8px 10px', fontSize: 13,
  border: '1px solid #D0CDC7', borderRadius: 6,
  outline: 'none', background: '#FFFFFF',
  color: '#2C2C2A', fontFamily: 'inherit',
  boxSizing: 'border-box'
}

const btnStyle = {
  fontSize: 12, padding: '6px 12px', borderRadius: 6,
  border: '1px solid #D0CDC7', background: 'transparent',
  color: '#4A4840', cursor: 'pointer'
}
